// <purpose>The Statistik page's THIRD ROW, the pure half: which child cards are mounted under the
// three bucket cards, how wide each column is, and which word a child wears. Three columns, one
// state each, the same in both lenses: nothing usable came out (Uploads · Ungeklärt), waiting on the
// clerk (Offen · Liegengeblieben), decided (Entschieden · Erledigt). One home per child word; the
// column says the state.</purpose>
// <constraints>
//   Shape rule (operator, 2026-10-28): mount the children that count in EITHER lens of this fetch;
//   size each column to its mounted children, a childless column keeps one unit; a mounted child
//   shows its word and number in both lenses, 0 included; the lens toggle never changes the shape,
//   only a refetch does (the page never polls). No child anywhere: two rows.
//   The server (core StatisticsProjection.BucketChildren) owns the children and their slot order
//   and serves every child, zeros included; this module never derives a count. A child key the
//   page has no word for is shown by its key, never dropped — a served fact must stay visible.
// </constraints>

import type { ActiveBucket, ArchiveBucket, LifecycleBucket } from '../../services/lifecycleBuckets';
import { STATUS_LABELS } from '../../constants';
import { DEVIATING_WORD, TO_ASSIGN_WORD, failedWord, unclassifiedWord } from './residueWords';

/** The child keys core serves (StatisticsProjection.BucketChild). */
export type BucketChildKey = 'failed' | 'unclassified' | 'copied' | 'approved' | 'deviating' | 'declined';

/** The served shape: bucket -> child -> count, in slot order. */
export type ServedBucketChildren = Partial<Record<LifecycleBucket, Record<string, number>>>;

export type MountedChild = { key: string; active: number; archive: number };
export type KpiColumn = { active: ActiveBucket; archive: ArchiveBucket; children: MountedChild[]; width: number };

/** The three columns, left to right: the active bucket on top, its archive twin underneath. */
export const KPI_COLUMNS: readonly { active: ActiveBucket; archive: ArchiveBucket }[] = [
  { active: 'uploads', archive: 'ungeklaert' },
  { active: 'offen', archive: 'liegengeblieben' },
  { active: 'entschieden', archive: 'erledigt' },
];

/** Children that count in either lens, in the served order (the active bucket's order first, then
 *  any key only the archive twin serves); width = max(1, mounted). */
export function mountedColumns(served: ServedBucketChildren | undefined): KpiColumn[] {
  return KPI_COLUMNS.map(c => {
    const a = served?.[c.active] ?? {};
    const b = served?.[c.archive] ?? {};
    const keys = [...Object.keys(a), ...Object.keys(b).filter(k => !(k in a))];
    const children = keys
      .map(k => ({ key: k, active: a[k] ?? 0, archive: b[k] ?? 0 }))
      .filter(ch => ch.active > 0 || ch.archive > 0);
    return { ...c, children, width: Math.max(1, children.length) };
  });
}

export const hasThirdRow = (columns: readonly KpiColumn[]): boolean => columns.some(c => c.children.length > 0);

/** The CSS grid template the three bucket cards and their children share, so a card always sits
 *  over its own children. */
export const columnTemplate = (columns: readonly KpiColumn[]): string =>
  columns.map(c => `minmax(0, ${c.width}fr)`).join(' ');

/** A child's word: a served face where one exists, a chrome word otherwise. */
export type ChildFace = { family: 'decision'; faceKey: 'approve' | 'copy' } | { text: string };

type WordMeta = { categoryKeys: string[]; isUnclassified: (key: string) => boolean; label: (key: string) => string };

/** `column` is the ACTIVE bucket of the child's column: the unclassified child is the clerk's to-do
 *  under Offen („Zuzuordnen") and the unresolved outcome under Ungeklärt (the vocabulary's word). */
export function childFace(key: string, cm: WordMeta, column: ActiveBucket = 'uploads'): ChildFace {
  switch (key) {
    case 'approved': return { family: 'decision', faceKey: 'approve' };
    case 'copied': return { family: 'decision', faceKey: 'copy' };
    case 'failed': return { text: failedWord() };
    case 'unclassified': return column === 'offen' ? { text: TO_ASSIGN_WORD } : { text: unclassifiedWord(cm, true) ?? key };
    case 'deviating': return { text: DEVIATING_WORD };
    case 'declined': return { text: STATUS_LABELS.declined };
    default: return { text: key };
  }
}
